/**
 * Share Room Component
 *
 * Displays QR code and share options for a room. Uses secure sharing that
 * does NOT expose the room secret in the URL.
 */

import { useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { createSecureShareData } from '@/lib/crypto/room-encryption'
import { QRCode } from './QRCode'

interface ShareRoomProps {
  roomId: string
  roomSecret: string
  roomName: string
  pin?: string
  isModal?: boolean
  onClose?: () => void
}

export function ShareRoom({
  roomId,
  roomSecret,
  roomName,
  pin,
  isModal = false,
  onClose,
}: ShareRoomProps) {
  const [linkCopied, setLinkCopied] = useState(false)
  const [secretCopied, setSecretCopied] = useState(false)
  const [pinCopied, setPinCopied] = useState(false)

  // Generate secure share data
  const shareData = useMemo(() => {
    console.log('[ShareRoom] Generating QR data:', {
      roomId,
      roomName,
      hasSecret: !!roomSecret,
      hasPin: !!pin,
    })
    return createSecureShareData(roomId, roomSecret, roomName, pin)
  }, [roomId, roomSecret, roomName, pin])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareData.joinUrl)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [shareData.joinUrl])

  const handleCopySecret = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareData.roomSecret)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [shareData.roomSecret])

  const handleCopyPin = useCallback(async () => {
    if (!pin) return
    try {
      await navigator.clipboard.writeText(pin)
      setPinCopied(true)
      setTimeout(() => setPinCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy PIN:', err)
    }
  }, [pin])

  const content = (
    <div className="flex flex-col items-center">
      {/* Room name */}
      <h2 className="text-xl font-display font-bold mb-1 uppercase tracking-widest text-primary neon-glow">{roomName}</h2>
      <p className="text-xs text-white/50 mb-6 font-mono">Share this room with others</p>

      {/* QR Code - contains all info for face-to-face sharing */}
      <div className="p-4 bg-white rounded-xl shadow-[0_0_30px_rgba(255,255,255,0.1)] relative">
        <div className="absolute -inset-1 border border-white/20 rounded-xl pointer-events-none"></div>
        <QRCode
          value={shareData.qrData}
          size={200}
          showDownload
          downloadFileName={`crowdtalkie-${roomName.toLowerCase().replace(/\s+/g, '-')}`}
          className="text-center"
        />
      </div>
      <p className="text-[10px] text-white/40 mt-3 font-mono border-t border-white/5 pt-2 uppercase tracking-wider">
        Scan to join instantly
      </p>

      {/* Security Notice */}
      <div className="w-full mt-6 p-3 rounded bg-green-500/10 border border-green-500/30">
        <p className="text-xs text-green-400 flex items-center gap-2 font-mono">
          <span className="animate-pulse">🔒</span>
          <span className="uppercase tracking-wide font-bold">Encrypted & Secure</span>
        </p>
      </div>

      {/* Room Link (without secret) */}
      <div className="w-full mt-4">
        <label className="text-[10px] text-primary uppercase tracking-wider mb-1 block font-bold">Room Link</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={shareData.joinUrl}
            readOnly
            className="flex-1 px-3 py-2 text-xs rounded bg-black/40 border border-white/10 text-white/70 font-mono focus:outline-none"
          />
          <button
            onClick={handleCopyLink}
            className={`px-4 py-2 rounded font-bold uppercase tracking-wider text-xs transition-colors border ${linkCopied
                ? 'bg-green-500/20 text-green-400 border-green-500/50'
                : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:text-white'
              }`}
          >
            {linkCopied ? 'COPIED' : 'COPY'}
          </button>
        </div>
      </div>

      {/* Room Secret (share via secure channel) */}
      <div className="w-full mt-4">
        <label className="text-[10px] text-yellow-400 uppercase tracking-wider mb-1 block font-bold">Room Secret</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={shareData.roomSecret.substring(0, 8) + '...'}
            readOnly
            className="flex-1 px-3 py-2 text-xs rounded bg-yellow-500/5 border border-yellow-500/20 text-yellow-100/70 font-mono focus:outline-none"
          />
          <button
            onClick={handleCopySecret}
            className={`px-4 py-2 rounded font-bold uppercase tracking-wider text-xs transition-colors border ${secretCopied
                ? 'bg-green-500/20 text-green-400 border-green-500/50'
                : 'bg-primary/20 text-primary border-primary/50 hover:bg-primary/30'
              }`}
          >
            {secretCopied ? 'COPIED' : 'COPY KEY'}
          </button>
        </div>
        <p className="text-[10px] text-yellow-400/60 mt-1 flex items-center gap-1 font-mono">
          <span>⚠</span> Share privately (text message, Signal, etc.)
        </p>
      </div>

      {/* PIN (if set) */}
      {pin && (
        <div className="w-full mt-4">
          <label className="text-[10px] text-red-400 uppercase tracking-wider mb-1 block font-bold">Access PIN</label>
          <div className="flex gap-2">
            <div className="flex-1 p-2 rounded bg-red-500/10 border border-red-500/30 flex items-center justify-between">
              <span className="font-mono text-lg font-bold tracking-[0.2em] text-red-200">
                {pin}
              </span>
              <button
                onClick={handleCopyPin}
                className={`px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors border ${pinCopied
                    ? 'bg-green-500/20 text-green-400 border-green-500/50'
                    : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
              >
                {pinCopied ? 'COPIED' : 'COPY'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="w-full mt-6 p-4 rounded glass-panel border border-white/10">
        <p className="text-xs text-primary font-bold uppercase tracking-wider mb-2">
          How to share:
        </p>
        <ul className="text-[10px] text-white/50 space-y-2 font-mono">
          <li className="flex gap-2">
            <span className="text-primary">1.</span>
            <span>In person: Have others scan the QR code</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">2.</span>
            <span>Remote: Send the Room Link via text or chat</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">3.</span>
            <span>For security, send the Secret separately from the Link</span>
          </li>
        </ul>
      </div>
    </div>
  )

  // Modal wrapper - Rendered via Portal for proper z-index stacking
  if (isModal) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] isolate flex items-center justify-center p-2 overflow-y-auto">
        {/* Backdrop with gradient */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-primary/10 via-black/95 to-black/95 backdrop-blur-md"
          onClick={onClose}
        />
        <div className="absolute inset-0 scanlines opacity-20 pointer-events-none"></div>

        <div
          className="w-full max-w-md bg-gradient-to-b from-[#1a1520] to-[#0d0a10] rounded-2xl border border-primary/30 max-h-[95vh] flex flex-col relative z-20 shadow-[0_0_80px_rgba(242,13,128,0.2)] my-auto animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sticky header with close button */}
          <div className="flex items-center justify-between p-4 border-b border-primary/20 bg-gradient-to-r from-primary/20 via-transparent to-primary/20 rounded-t-2xl sticky top-0 z-10">
            <span className="text-sm text-white font-bold uppercase tracking-wider flex items-center gap-2">
              <span className="text-lg">🔗</span> Share Room
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all border border-white/10 hover:border-white/30"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Scrollable content */}
          <div className="p-4 overflow-y-auto flex-1 scrollbar-hide">
            {content}
          </div>

          {/* Sticky footer with close button */}
          <div className="p-4 border-t border-primary/20 bg-black/30 rounded-b-2xl sticky bottom-0">
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-primary/80 text-white font-bold border border-primary/50 hover:from-primary/90 hover:to-primary/70 transition-all uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(242,13,128,0.4)]"
            >
              Done
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // Inline content
  return content
}
